import { action, observable } from 'mobx';

import i18n from '@link/emot/i18n';
import { linktrainrelay } from '@link/emot/services';
import { SeatSelectionMode } from '@link/emot/services/types';
import type {
  BookingInfo,
  BookingSelection,
  CommonReq,
  Diagram,
  GetOrderParametersResp,
  SeatClass,
  SeatMapSeat,
  SeatPreference,
  Station,
  TimeRange
} from '@link/emot/services/types';
import { getOrderLocaleLanguage } from '@link/emot/utils';

export class Emot {
  @observable accessor bookingId = '';
  @observable accessor token = '';
  @observable accessor tUrl = '';
  @observable accessor orderType: 'booking' | 'change' = 'booking';
  @observable accessor bookingInfo: BookingInfo | null = null;
  @observable accessor depStations: Station[] = [];
  @observable accessor arrStations: Station[] = [];
  @observable accessor diagrams: Diagram[] = [];
  @observable accessor orderableTimeRanges: TimeRange[] = [];
  @observable accessor orderParameters: GetOrderParametersResp | null = null;
  @observable accessor seatMap: SeatMapSeat[] = [];
  @observable accessor seatMapWidth = 0;
  @observable accessor selection: BookingSelection = {};

  private bookingRequestId = 0;
  private stationsRequestId = 0;
  private diagramsRequestId = 0;
  private orderParametersRequestId = 0;
  private seatMapRequestId = 0;

  private get common(): CommonReq {
    return {
      bookingId: this.bookingId || undefined,
      token: this.token || undefined,
      locale: { language: getOrderLocaleLanguage(i18n.language) }
    };
  }

  @action
  setBookingId(bookingId: string) {
    if (this.bookingId && this.bookingId !== bookingId) {
      this.resetBookingState();
    }
    this.bookingId = bookingId;
  }

  @action
  setToken(token: string) {
    this.token = token;
  }

  @action
  setTUrl(tUrl: string) {
    this.tUrl = tUrl;
  }

  @action
  setOrderType(orderType: 'booking' | 'change') {
    this.orderType = orderType;
  }

  @action
  getBookingData = async (bookingId?: string) => {
    const orderNo = bookingId || this.bookingId;
    if (!orderNo) return null;

    if (this.bookingId && this.bookingId !== orderNo) {
      this.resetBookingState();
    }
    this.bookingId = orderNo;
    const requestId = ++this.bookingRequestId;
    const requestToken = this.token;

    const detail = await linktrainrelay.getOrderDetails({
      commonBookingId: orderNo,
      commonToken: requestToken || undefined,
      commonLocaleLanguage: getOrderLocaleLanguage(i18n.language)
    });
    if (
      requestId !== this.bookingRequestId ||
      this.bookingId !== orderNo ||
      this.token !== requestToken
    ) {
      return null;
    }
    const bookingInfo = { ...detail, bookingId: orderNo };
    this.setBookingData(bookingInfo);

    const depStations = detail.depStations ?? [];
    const arrStations = detail.arrStations ?? [];
    this.setStationList(depStations, arrStations);

    if (
      !this.selection.fromStation &&
      depStations.length &&
      arrStations.length
    ) {
      this.selection = {
        ...this.selection,
        fromStation:
          depStations.find(
            station => station.code === detail.fromStationCode
          ) ?? depStations[0],
        toStation:
          arrStations.find(station => station.code === detail.toStationCode) ??
          arrStations[0]
      };
    }
    return bookingInfo;
  };

  @action
  getStations = async () => {
    const requestId = ++this.stationsRequestId;
    const requestBookingId = this.bookingId;
    const requestToken = this.token;
    const response = await linktrainrelay.getStationsRaw({
      commonBookingId: requestBookingId || undefined,
      commonToken: requestToken || undefined,
      commonLocaleLanguage: getOrderLocaleLanguage(i18n.language)
    });
    const json = (await response.raw.json()) as {
      orderable_time_ranges?: { from?: string; to?: string }[];
    };
    const ranges = (json.orderable_time_ranges ?? [])
      .filter((range): range is TimeRange => !!range.from && !!range.to)
      .map(range => ({ from: range.from, to: range.to }));
    if (
      requestId !== this.stationsRequestId ||
      this.bookingId !== requestBookingId ||
      this.token !== requestToken
    ) {
      return [];
    }
    this.setOrderableTimeRanges(ranges);
    return ranges;
  };

  @action
  searchDiagrams = async (range?: TimeRange) => {
    const requestId = ++this.diagramsRequestId;
    const requestBookingId = this.bookingId;
    const requestToken = this.token;
    const requestFromCode = this.selection.fromStation?.code;
    const requestToCode = this.selection.toStation?.code;
    const requestDepartDate = this.bookingInfo?.departDate;
    const requestRangeFrom = range?.from;
    const requestRangeTo = range?.to;
    const { diagrams = [] } = await linktrainrelay.searchDiagrams({
      body: {
        common: this.common,
        depStationCode: requestFromCode,
        arrStationCode: requestToCode,
        depDate: requestDepartDate,
        depTimeFrom: requestRangeFrom,
        depTimeTo: requestRangeTo
      }
    });
    if (
      requestId !== this.diagramsRequestId ||
      this.bookingId !== requestBookingId ||
      this.token !== requestToken ||
      this.selection.fromStation?.code !== requestFromCode ||
      this.selection.toStation?.code !== requestToCode ||
      this.bookingInfo?.departDate !== requestDepartDate
    ) {
      return { diagrams: [] };
    }
    this.setDiagrams(diagrams);
    return { diagrams };
  };

  @action
  getOrderParameters = async () => {
    const requestId = ++this.orderParametersRequestId;
    const requestBookingId = this.bookingId;
    const requestDiagramCode = this.selection.diagram?.diagramCode;
    const requestSeatClassId = this.selection.seatClass?.seatClassId;
    const response = await linktrainrelay.getOrderParameters({
      body: {
        common: this.common,
        diagramCode: requestDiagramCode,
        seatClassId: requestSeatClassId
      }
    });
    if (
      requestId !== this.orderParametersRequestId ||
      this.bookingId !== requestBookingId ||
      this.selection.diagram?.diagramCode !== requestDiagramCode ||
      this.selection.seatClass?.seatClassId !== requestSeatClassId
    ) {
      return response;
    }
    this.setOrderParameters(response);
    return response;
  };

  @action
  getSeatMap = async () => {
    const requestId = ++this.seatMapRequestId;
    const requestBookingId = this.bookingId;
    const requestDiagramCode = this.selection.diagram?.diagramCode;
    const requestSeatClassId = this.selection.seatClass?.seatClassId;
    const requestCarId = this.selection.carId;
    const response = await linktrainrelay.getSeatMap({
      body: {
        common: this.common,
        diagramCode: requestDiagramCode,
        seatClassId: requestSeatClassId,
        carId: requestCarId
      }
    });
    if (
      requestId !== this.seatMapRequestId ||
      this.bookingId !== requestBookingId ||
      this.selection.diagram?.diagramCode !== requestDiagramCode ||
      this.selection.seatClass?.seatClassId !== requestSeatClassId ||
      this.selection.carId !== requestCarId
    ) {
      return response;
    }
    this.setSeatMap(response.seats ?? [], response.width ?? 0);
    return response;
  };

  @action
  submitBooking = () => {
    const {
      diagram,
      seatSelectionMode,
      seatPreference,
      carId,
      selectedSeatIds
    } = this.selection;

    return linktrainrelay.createOrder({
      body: {
        common: this.common,
        diagramCode: diagram?.diagramCode,
        seatPreferenceCode:
          seatSelectionMode === SeatSelectionMode.Preference
            ? seatPreference?.code
            : undefined,
        carId:
          seatSelectionMode === SeatSelectionMode.SeatMap
            ? carId || undefined
            : undefined,
        seatCodes:
          seatSelectionMode === SeatSelectionMode.SeatMap
            ? selectedSeatIds
            : undefined
      }
    });
  };

  @action
  submitChange = () => {
    const {
      diagram,
      seatSelectionMode,
      seatPreference,
      carId,
      selectedSeatIds
    } = this.selection;

    return linktrainrelay.changeOrder({
      body: {
        common: this.common,
        diagramCode: diagram?.diagramCode,
        seatPreferenceCode:
          seatSelectionMode === SeatSelectionMode.Preference
            ? seatPreference?.code
            : undefined,
        carId:
          seatSelectionMode === SeatSelectionMode.SeatMap
            ? carId || undefined
            : undefined,
        seatCodes:
          seatSelectionMode === SeatSelectionMode.SeatMap
            ? selectedSeatIds
            : undefined
      }
    });
  };

  @action
  setBookingData(detail: BookingInfo) {
    this.bookingInfo = detail;
  }

  @action
  setStationList(depStations: Station[], arrStations: Station[]) {
    this.depStations = depStations;
    this.arrStations = arrStations;
  }

  @action
  setDiagrams(diagrams: Diagram[]) {
    this.diagrams = diagrams;
  }

  @action
  setOrderableTimeRanges(orderableTimeRanges: TimeRange[]) {
    this.orderableTimeRanges = orderableTimeRanges;
  }

  @action
  setOrderParameters(orderParameters: GetOrderParametersResp) {
    this.orderParameters = orderParameters;
  }

  @action
  setSeatMap(seats: SeatMapSeat[], width: number) {
    this.seatMap = seats;
    this.seatMapWidth = width;
  }

  @action
  setStations(fromStation: Station, toStation: Station) {
    this.selection = { ...this.selection, fromStation, toStation };
    this.clearFollowingSelection();
  }

  @action
  swapStations() {
    const { fromStation, toStation, routeReversed } = this.selection;
    this.selection = {
      ...this.selection,
      fromStation: toStation,
      toStation: fromStation,
      routeReversed: !routeReversed
    };
    this.clearFollowingSelection();
  }

  @action
  setDiagram(diagram: Diagram) {
    const seatClass =
      diagram.seatClasses?.find(
        item => item.seatClassName === this.bookingInfo?.seatClassName
      ) ?? diagram.seatClasses?.[0];
    this.selection = {
      ...this.selection,
      diagram,
      seatClass,
      seatSelectionMode: undefined,
      seatPreference: undefined,
      carId: undefined,
      selectedSeatIds: []
    };
    this.orderParameters = null;
    this.seatMap = [];
  }

  @action
  setSeatClass(seatClass: SeatClass) {
    this.selection = { ...this.selection, seatClass };
  }

  @action
  setSeatSelectionMode(seatSelectionMode: SeatSelectionMode) {
    this.selection = {
      ...this.selection,
      seatSelectionMode,
      seatPreference: undefined,
      carId: undefined,
      selectedSeatIds: []
    };
  }

  @action
  setSeatPreference(seatPreference: SeatPreference) {
    this.selection = { ...this.selection, seatPreference };
  }

  @action
  setCarId(carId: string) {
    this.selection = { ...this.selection, carId, selectedSeatIds: [] };
  }

  @action
  toggleSeat(seatId: string, maxSeats: number) {
    const selected = this.selection.selectedSeatIds ?? [];
    const next = selected.includes(seatId)
      ? selected.filter(id => id !== seatId)
      : selected.length < maxSeats
        ? [...selected, seatId]
        : selected;
    this.selection = { ...this.selection, selectedSeatIds: next };
  }

  private clearFollowingSelection() {
    this.diagrams = [];
    this.orderParameters = null;
    this.seatMap = [];
    this.selection = {
      ...this.selection,
      diagram: undefined,
      seatClass: undefined,
      seatSelectionMode: undefined,
      seatPreference: undefined,
      carId: undefined,
      selectedSeatIds: []
    };
  }

  private resetBookingState() {
    this.bookingRequestId += 1;
    this.stationsRequestId += 1;
    this.diagramsRequestId += 1;
    this.orderParametersRequestId += 1;
    this.seatMapRequestId += 1;
    this.bookingInfo = null;
    this.depStations = [];
    this.arrStations = [];
    this.diagrams = [];
    this.orderableTimeRanges = [];
    this.orderParameters = null;
    this.seatMap = [];
    this.seatMapWidth = 0;
    this.selection = {};
    this.orderType = 'booking';
    this.tUrl = '';
  }

  @action
  resetSelection() {
    this.selection = {};
  }
}

export default new Emot();
